import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { LinearProgress } from 'beam/dist/Progress'
import Typography from 'beam/dist/Typography'
import { makeStyles } from 'beam'

/** @deprecated use "EMPTY" as the default when setting strength */
export const PasswordStrength = {
  EMPTY: 'EMPTY',
  TOOSHORT: 'TOOSHORT',
  WEAK: 'WEAK',
  GOOD: 'GOOD',
  STRONG: 'STRONG',
}

export type StrengthT = 'EMPTY' | 'TOOSHORT' | 'WEAK' | 'GOOD' | 'STRONG'

interface ClassDataT{
  className: 'empty' | 'tooShort' | 'weak' | 'good' | 'strong' | 'progressBar' | 'description',
  label: '' | 'passwordTooShort' | 'weakPassword' | 'good' | 'strongPassword',
  value: 0 | 25 | 50 | 75 | 100,
}

const getStrengthData = (strength: StrengthT): ClassDataT => {
  switch (strength) {
    case 'EMPTY':
      return {
        label: '',
        value: 0,
        className: 'empty',
      }
    case 'TOOSHORT':
      return {
        label: 'passwordTooShort',
        value: 25,
        className: 'tooShort',
      }
    case 'WEAK':
      return {
        label: 'weakPassword',
        value: 50,
        className: 'weak',
      }
    case 'GOOD':
      return {
        label: 'good',
        value: 75,
        className: 'good',
      }
    case 'STRONG':
      return {
        label: 'strongPassword',
        value: 100,
        className: 'strong',
      }
    default:
      return {
        label: '',
        value: 0,
        className: 'empty',
      }
  }
}

const useStyles = makeStyles(theme => ({
  progressBar: {
    transition: 'background-color 0.5s ease',
  },
  description: {
    width: '300px',
  },
  empty: {
    backgroundColor: '',
  },
  tooShort: {
    backgroundColor: theme.palette.error.main,
  },
  weak: {
    backgroundColor: theme.palette.error.light,
  },
  good: {
    backgroundColor: theme.palette.primary.light,
  },
  strong: {
    backgroundColor: theme.palette.primary.main,
  },
}))

interface PasswordStrengthIndicatorT {
  strength: StrengthT,
  width?: number,
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorT> = ({ strength, width }) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const data = getStrengthData(strength)
  return (
    <div style={{ width }}>
      <LinearProgress
        variant="determinate"
        className={classes.progressBar}
        classes={{
          barColorPrimary: data?.className ? classes[data.className] : '',
        }}
        value={data.value}
      />
      <Typography className={classes.description} variant="caption">
        {data.label ? t(data.label) : ''}
      </Typography>
    </div>
  )
}

PasswordStrengthIndicator.propTypes = {
  strength: PropTypes.oneOf<StrengthT>(['EMPTY', 'TOOSHORT', 'WEAK', 'GOOD', 'STRONG']).isRequired,
}

PasswordStrengthIndicator.defaultProps = {
  strength: 'EMPTY',
}

export default PasswordStrengthIndicator
